import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { scrollToSection } from "@/lib/scroll";
import { GhostCTA, PrimaryCTA } from "@/components/shared/CTA";
import { track, EVENTS } from "@/lib/analytics";

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.16, delayChildren: 1.9 } },
};
const lineReveal = {
  hidden: { y: "115%" },
  show: { y: "0%", transition: { duration: 1.15, ease: [0.16, 1, 0.3, 1] } },
};
const fadeUp = {
  hidden: { y: 30, opacity: 0 },
  show: { y: 0, opacity: 1, transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } },
};
const INDICATOR_INITIAL = { opacity: 0 };
const INDICATOR_ANIMATE = { opacity: 1 };
const INDICATOR_TRANSITION = { delay: 3.2, duration: 1 };

export default function Hero() {
  return (
    <section
      id="experience"
      data-testid="hero-section"
      className="relative z-10 flex min-h-screen flex-col justify-center overflow-hidden px-6 pt-24 md:px-16 lg:px-24"
    >
      <div className="hero-vignette pointer-events-none absolute inset-0" />
      <motion.div variants={container} initial="hidden" animate="show" className="relative">
        <motion.p
          variants={fadeUp}
          data-testid="hero-overline"
          className="mb-8 font-mono text-[11px] uppercase tracking-[0.45em] text-[#06B6D4]"
        >
          Est. 2026 — Creative Technology Studio
        </motion.p>
        <h1 className="font-display font-black uppercase leading-[0.95] tracking-tight">
          <span className="block overflow-hidden">
            <motion.span
              variants={lineReveal}
              data-testid="hero-title"
              className="block bg-gradient-to-r from-[#F4F6FB] via-[#c9b8f5] to-[#7dd8ea] bg-clip-text text-[12.5vw] text-transparent md:text-[11vw] lg:text-[9.5rem]"
            >
              Elentara
            </motion.span>
          </span>
          <span className="mt-4 block overflow-hidden md:mt-6">
            <motion.span
              variants={lineReveal}
              data-testid="hero-tagline"
              className="block text-xl font-light normal-case tracking-normal text-[#F4F6FB]/85 md:text-3xl lg:text-4xl"
            >
              Digital experiences beyond the expected.
            </motion.span>
          </span>
          <span className="sr-only">
            Elentara — creative technology studio for immersive web, 3D and interactive systems
          </span>
        </h1>
        <motion.p
          variants={fadeUp}
          data-testid="hero-service-line"
          className="mt-10 max-w-2xl font-mono text-[10px] uppercase leading-relaxed tracking-[0.28em] text-[#F4F6FB]/45 md:text-[11px]"
        >
          Immersive Web · Creative Technology · 3D · Spatial Computing · Interactive Systems
        </motion.p>
        <motion.p
          variants={fadeUp}
          data-testid="hero-subcopy"
          className="mt-6 max-w-md text-sm leading-relaxed text-[#F4F6FB]/55 md:text-base"
        >
          A creative technology studio building immersive digital experiences, generative systems
          and spatial interfaces — for brands that refuse to look like everyone else.
        </motion.p>
        <motion.div variants={fadeUp} className="mt-12 flex flex-wrap items-center gap-5">
          <GhostCTA to="/work" testId="hero-explore-button">
            Explore Work
          </GhostCTA>
          <PrimaryCTA
            to="/start-a-project"
            testId="hero-enter-button"
            onClick={() => track(EVENTS.START_PROJECT_CLICK, { from: "hero" })}
          >
            Start a Project
          </PrimaryCTA>
        </motion.div>
      </motion.div>
      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToSection("#manifesto")}
        className="absolute bottom-10 left-1/2 z-10 hidden -translate-x-1/2 flex-col items-center gap-3 md:flex"
        initial={INDICATOR_INITIAL}
        animate={INDICATOR_ANIMATE}
        transition={INDICATOR_TRANSITION}
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#F4F6FB]/40">Scroll</span>
        <span className="scroll-line relative block h-14 w-px overflow-hidden bg-white/10" />
        <ChevronDown className="h-4 w-4 animate-bounce text-[#8B5CF6]" />
      </motion.button>
    </section>
  );
}
